import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Download,
  Users,
  GraduationCap,
  BookOpen,
  Layers,
  FileCheck,
  MessageSquare,
  HelpCircle,
  Award,
  Trophy,
  Filter,
  X,
  CheckCircle2,
  ClipboardCheck,
  BarChart3,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useLearningAnalytics, useLearningFilterOptions, LearningFilters } from "@/hooks/useLearningAnalytics";
import { LearnerSearch } from "@/components/admin/learning/LearnerSearch";
import { LearnerProgressPanel } from "@/components/admin/learning/LearnerProgressPanel";
import { exportAllLearnersCSV } from "@/utils/exportAllLearnersCSV";

export function LearningAnalytics() {
  const [filters, setFilters] = useState<LearningFilters>({
    userType: "all",
    curriculumId: null,
    courseId: null,
    moduleId: null,
    cohortId: null,
  });
  const [selectedLearnerId, setSelectedLearnerId] = useState<string | null>(null);
  const [isExportingAll, setIsExportingAll] = useState(false);

  const { data: analytics, isLoading } = useLearningAnalytics(filters);
  const { curricula, courses, modules, cohorts } = useLearningFilterOptions();


  // Filter courses based on selected curriculum
  const filteredCourses = useMemo(() => {
    if (!courses) return [];
    if (!filters.curriculumId) return courses;
    return courses.filter((c) => c.curriculum_id === filters.curriculumId);
  }, [courses, filters.curriculumId]);

  // Filter modules based on selected course
  const filteredModules = useMemo(() => {
    if (!modules) return [];
    if (!filters.courseId) return modules;
    return modules.filter((m) => m.course_id === filters.courseId);
  }, [modules, filters.courseId]);

  const clearFilters = () => {
    setFilters({
      userType: "all",
      curriculumId: null,
      courseId: null,
      moduleId: null,
      cohortId: null,
    });
  };

  const hasActiveFilters =
    filters.userType !== "all" ||
    filters.curriculumId !== null ||
    filters.courseId !== null ||
    filters.moduleId !== null ||
    filters.cohortId !== null;


  const exportToCSV = () => {
    if (!analytics) {
      toast.error("No data to export");
      return;
    }

    const rows: string[][] = [];
    
    // Summary section
    rows.push(["Learning Analytics Summary"]);
    rows.push(["Generated", format(new Date(), "yyyy-MM-dd HH:mm")]);
    rows.push(["User Type Filter", filters.userType]);
    rows.push([""]);
    
    rows.push(["Metric", "Value", "Details"]);
    rows.push(["Total Learners", analytics.totalLearners.toString(), ""]);
    rows.push([
      "Curriculum Enrollments",
      analytics.curriculumEnrollments.total.toString(),
      `${analytics.curriculumEnrollments.completed} completed`,
    ]);
    rows.push([
      "Course Enrollments",
      analytics.courseEnrollments.total.toString(),
      `${analytics.courseEnrollments.completed} completed`,
    ]);
    rows.push([
      "Module Progress",
      analytics.modulesProgress.total.toString(),
      `${analytics.modulesProgress.completed} completed`,
    ]);
    rows.push([
      "Quizzes",
      analytics.quizzes.total.toString(),
      `${analytics.quizzes.passed} passed, avg score: ${analytics.quizzes.avgScore}%`,
    ]);
    rows.push(["Assignments Submitted", analytics.assignments.submitted.toString(), ""]);
    rows.push([
      "Discussions",
      analytics.discussions.created.toString(),
      `${analytics.discussions.replies} replies`,
    ]);
    rows.push([
      "Help Requests",
      analytics.helpRequests.total.toString(),
      `${analytics.helpRequests.resolved} resolved`,
    ]);
    rows.push(["Certificates Issued", analytics.certificatesIssued.toString(), ""]);
    rows.push([""]);
    
    // Most active users
    rows.push(["Most Active Learners"]);
    rows.push(["Rank", "Name", "Role", "Modules", "Quizzes", "Assignments", "Discussions", "Activity Score"]);
    analytics.mostActiveUsers.forEach((user, index) => {
      rows.push([
        (index + 1).toString(),
        user.fullName,
        user.role,
        user.modulesCompleted.toString(),
        user.quizzesPassed.toString(),
        user.assignmentsSubmitted.toString(),
        (user.discussionsCreated + user.discussionReplies).toString(),
        user.activityScore.toString(),
      ]);
    });

    const csvContent = rows
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `learning-analytics-${format(new Date(), "yyyy-MM-dd")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("Learning analytics exported!");
  };

  const calcPercentage = (value: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-2 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <GraduationCap className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Learning Analytics</h3>
            {!selectedLearnerId && analytics && (
              <Badge variant="secondary">{analytics.totalLearners} learners</Badge>
            )}
            {selectedLearnerId && (
              <Badge variant="outline" className="flex items-center gap-1">
                Individual View
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedLearnerId ? (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setSelectedLearnerId(null)}
              >
                <X className="h-4 w-4 mr-2" />
                Clear Selection
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={async () => {
                    setIsExportingAll(true);
                    await exportAllLearnersCSV();
                    setIsExportingAll(false);
                  }}
                  disabled={isExportingAll}
                >
                  {isExportingAll ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Users className="h-4 w-4 mr-2" />
                  )}
                  Export All Learners
                </Button>
                <Button variant="outline" size="sm" onClick={exportToCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Export Summary
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Learner Search */}
        <div className="p-3 bg-muted/50 rounded-lg">
          <LearnerSearch 
            onSelectLearner={setSelectedLearnerId}
            selectedLearnerId={selectedLearnerId}
          />
        </div>
      </div>

      {/* Show individual learner progress or aggregate analytics */}
      {selectedLearnerId ? (
        <LearnerProgressPanel learnerId={selectedLearnerId} />
      ) : (
        <>
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 p-3 bg-muted/50 rounded-lg">
          <Filter className="h-4 w-4 text-muted-foreground" />
          
          <Select
            value={filters.userType}
            onValueChange={(value) =>
              setFilters((f) => ({ ...f, userType: value as LearningFilters["userType"] }))
            }
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="User Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="entrepreneur">Entrepreneurs</SelectItem>
              <SelectItem value="mentor">Mentors</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.cohortId || "all"}
            onValueChange={(value) =>
              setFilters((f) => ({ ...f, cohortId: value === "all" ? null : value }))
            }
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Cohorts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cohorts</SelectItem>
              {cohorts?.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{!c.is_active ? " (inactive)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>



          <Select
            value={filters.curriculumId || "all"}
            onValueChange={(value) =>
              setFilters((f) => ({
                ...f,
                curriculumId: value === "all" ? null : value,
                courseId: null,
                moduleId: null,
              }))
            }
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Curricula" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Curricula</SelectItem>
              {curricula?.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.courseId || "all"}
            onValueChange={(value) =>
              setFilters((f) => ({
                ...f,
                courseId: value === "all" ? null : value,
                moduleId: null,
              }))
            }
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Courses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {filteredCourses?.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.moduleId || "all"}
            onValueChange={(value) =>
              setFilters((f) => ({ ...f, moduleId: value === "all" ? null : value }))
            }
            disabled={!filters.courseId}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Modules" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modules</SelectItem>
              {filteredModules?.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Learners</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalLearners || 0}</div>
            <p className="text-xs text-muted-foreground">Enrolled in courses/curricula</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Curriculum Enrollments</CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.curriculumEnrollments.total || 0}</div>
            <p className="text-xs text-muted-foreground">
              {analytics?.curriculumEnrollments.completed || 0} completed (
              {calcPercentage(
                analytics?.curriculumEnrollments.completed || 0,
                analytics?.curriculumEnrollments.total || 0
              )}
              %)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Course Enrollments</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.courseEnrollments.total || 0}</div>
            <p className="text-xs text-muted-foreground">
              {analytics?.courseEnrollments.completed || 0} completed (
              {calcPercentage(
                analytics?.courseEnrollments.completed || 0,
                analytics?.courseEnrollments.total || 0
              )}
              %)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Certificates Issued</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.certificatesIssued || 0}</div>
            <p className="text-xs text-muted-foreground">Total certificates awarded</p>
          </CardContent>
        </Card>
      </div>

      {/* Completion & Engagement Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Modules Completed */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Module Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Completed</span>
              <span className="text-muted-foreground">
                {analytics?.modulesProgress.completed || 0} / {analytics?.modulesProgress.total || 0}
              </span>
            </div>
            <Progress
              value={calcPercentage(
                analytics?.modulesProgress.completed || 0,
                analytics?.modulesProgress.total || 0
              )}
              className="h-2"
            />
            <p className="text-xs text-muted-foreground">
              {calcPercentage(
                analytics?.modulesProgress.completed || 0,
                analytics?.modulesProgress.total || 0
              )}
              % completion rate
            </p>
          </CardContent>
        </Card>

        {/* Quizzes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileCheck className="h-4 w-4" />
              Quiz Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Passed</span>
              <span className="text-muted-foreground">
                {analytics?.quizzes.passed || 0} / {analytics?.quizzes.total || 0}
              </span>
            </div>
            <Progress
              value={calcPercentage(analytics?.quizzes.passed || 0, analytics?.quizzes.total || 0)}
              className="h-2"
            />
            <p className="text-xs text-muted-foreground">
              Average score: {analytics?.quizzes.avgScore || 0}%
            </p>
          </CardContent>
        </Card>

        {/* Assignments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" />
              Assignments
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-2xl font-bold">{analytics?.assignments.submitted || 0}</div>
            <p className="text-sm text-muted-foreground">Total submissions</p>
          </CardContent>
        </Card>

        {/* Discussions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Discussions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-2xl font-bold">{analytics?.discussions.created || 0}</div>
                <p className="text-xs text-muted-foreground">Topics created</p>
              </div>
              <div>
                <div className="text-2xl font-bold">{analytics?.discussions.replies || 0}</div>
                <p className="text-xs text-muted-foreground">Replies</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help Requests */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              Help Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Resolved</span>
              <span className="text-muted-foreground">
                {analytics?.helpRequests.resolved || 0} / {analytics?.helpRequests.total || 0}
              </span>
            </div>
            <Progress
              value={calcPercentage(
                analytics?.helpRequests.resolved || 0,
                analytics?.helpRequests.total || 0
              )}
              className="h-2"
            />
            <p className="text-xs text-muted-foreground">
              {calcPercentage(
                analytics?.helpRequests.resolved || 0,
                analytics?.helpRequests.total || 0
              )}
              % resolution rate
            </p>
          </CardContent>
        </Card>

        {/* Engagement Score Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Engagement Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Total Activities</span>
              <span className="font-medium">
                {(analytics?.modulesProgress.completed || 0) +
                  (analytics?.quizzes.passed || 0) +
                  (analytics?.assignments.submitted || 0) +
                  (analytics?.discussions.created || 0) +
                  (analytics?.discussions.replies || 0)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Avg per Learner</span>
              <span className="font-medium">
                {analytics?.totalLearners
                  ? (
                      ((analytics?.modulesProgress.completed || 0) +
                        (analytics?.quizzes.passed || 0) +
                        (analytics?.assignments.submitted || 0) +
                        (analytics?.discussions.created || 0) +
                        (analytics?.discussions.replies || 0)) /
                      analytics.totalLearners
                    ).toFixed(1)
                  : 0}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Most Active Learners */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Most Active Learners
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analytics?.mostActiveUsers && analytics.mostActiveUsers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Rank</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-center">Modules</TableHead>
                  <TableHead className="text-center">Quizzes</TableHead>
                  <TableHead className="text-center">Assignments</TableHead>
                  <TableHead className="text-center">Discussions</TableHead>
                  <TableHead className="text-right">Activity Score</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics.mostActiveUsers.map((user, index) => (
                  <TableRow key={user.userId}>
                    <TableCell>
                      <div className="flex items-center justify-center">
                        {index === 0 && <span className="text-xl">🥇</span>}
                        {index === 1 && <span className="text-xl">🥈</span>}
                        {index === 2 && <span className="text-xl">🥉</span>}
                        {index > 2 && <span className="text-muted-foreground">{index + 1}</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatarUrl || undefined} />
                          <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                            {user.fullName.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{user.fullName}</p>
                          <Badge variant="outline" className="text-xs capitalize">
                            {user.role}
                          </Badge>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{user.modulesCompleted}</TableCell>
                    <TableCell className="text-center">{user.quizzesPassed}</TableCell>
                    <TableCell className="text-center">{user.assignmentsSubmitted}</TableCell>
                    <TableCell className="text-center">
                      {user.discussionsCreated + user.discussionReplies}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary" className="font-mono">
                        {user.activityScore}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No activity data available yet
            </p>
          )}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
